import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useContent } from '../contexts/ContentContext';
import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
  whatsappToWaDigits,
  getSiteBranding,
  sanitizeStoredContact,
  formatContactAddress
} from '../config/siteConfig';
import { buildOrganizationSchema, buildWebSiteSchema, toJsonLd } from '../config/geoSchema';
import GlowIcon from './GlowIcons';

export * from '../config/siteConfig';
export { default as GlowIcon } from './GlowIcons';


export { default as LightningBackdrop } from './LightningBackdrop';
export { default as InViewPause } from './InViewPause';
export {
  SeoContent,
  SeoIntro,
  SeoSectionsGrid,
  SeoSection,
  SeoOutro
} from './SeoContentLayout';

/** Navbar / footer — seoSettings (admin) veya varsayılan marka */
export const SiteLogo = ({
  className = 'logo',
  as: Tag = 'div',
  onClick,
  linkTo,
  showBrandText: showBrandTextProp
}) => {
  const { seoSettings } = useContent();
  const branding = getSiteBranding(seoSettings);
  const { brandName, logoUrl, showLogoImage, showBrandText: showBrandTextFromSettings } = branding;
  const displayName = brandName || SITE_NAME;
  const showBrandText =
    showBrandTextProp !== undefined ? showBrandTextProp : showBrandTextFromSettings;

  const inner = (
    <>
      {showLogoImage && logoUrl && (
        <img
          src={logoUrl}
          alt={displayName}
          className="logo-img"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      {showBrandText && <span className="logo-text">{displayName}</span>}
    </>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className={className} onClick={onClick}>
        {inner}
      </Link>
    );
  }

  return (
    <Tag className={className} onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}>
      {inner}
    </Tag>
  );
};

// Scroll to Top
export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [pathname]);

  return null;
};

// Navbar
export const Navbar = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: '/', label: 'Ana Sayfa' },
    { to: '/paketler', label: 'Paketler' },
    { to: '/bayilik', label: 'Bayilik' },
    { to: '/kurulum', label: 'Kurulum' },
    { to: '/sss', label: 'SSS' },
    { to: '/hakkimizda', label: 'Hakkımızda' }
  ];

  return (
    <nav className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
      <div className="container">
        <SiteLogo
          className="logo logo-navbar"
          linkTo="/"
          showBrandText={false}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <button
          type="button"
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
          aria-expanded={isMobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        {isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          ></div>
        )}
        <div className="nav-menu-shell">
          <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
            {navItems.map(({ to, label }) => (
              <li key={to}>
                <Link
                  to={to}
                  className={`nav-link ${location.pathname === to ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

// Footer
export const Footer = () => {
  const { contactInfo, seoSettings } = useContent();
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  const contact = sanitizeStoredContact(contactInfo);
  const addressLine = formatContactAddress(contact.address);
  const email = contact.email;
  const whatsapp = contact.whatsapp;
  const workingHours = contact.workingHours;

  const handleLogoClick = (e) => {
    e.preventDefault();
    const now = Date.now();

    if (now - lastClickTime > 3000) {
      setClickCount(1);
    } else {
      setClickCount(prev => prev + 1);
    }

    setLastClickTime(now);

    if (clickCount + 1 === 8) {
      window.location.href = '/admin/login';
      setClickCount(0);
    }
  };

  const whatsappDigits = whatsappToWaDigits(whatsapp);

  const brandName = getSiteBranding(seoSettings).brandName || SITE_NAME;

  return (
    <footer className="footer footer-colorful footer-premium">
      <div className="footer-glow" aria-hidden="true"></div>
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <SiteLogo className="logo logo-link" onClick={handleLogoClick} />
            <p className="footer-tagline">
              <strong>{brandName}</strong> — Yeni nesil IPTV altyapısı ile 4K Ultra HD kalitesinde kesintisiz canlı yayın, film ve dizi arşivi. Türkiye ve Avrupa genelinde yüksek hızlı, donmasız ve güvenilir yayın deneyimi.
            </p>
          </div>

          <div className="footer-links">
            <h4 className="footer-heading">Hızlı Erişim</h4>
            <ul className="footer-list">
              <li><Link to="/">Ana Sayfa</Link></li>
              <li><Link to="/paketler">Paket Fiyatları & Abonelik</Link></li>
              <li><Link to="/kurulum">Cihaz Kurulum Rehberi</Link></li>
              <li><Link to="/bayilik">Bayilik & Panel Yönetimi</Link></li>
              <li><Link to="/hakkimizda">Altyapı & Hakkımızda</Link></li>
              <li><Link to="/sss">Sık Sorulan Sorular</Link></li>
            </ul>
          </div>

          <div className="footer-contact">
            <h4 className="footer-heading">İletişim & Destek</h4>
            <ul className="contact-info">
              {addressLine ? (
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="footer-ic" aria-hidden="true" style={{ display: 'inline-flex' }}><GlowIcon name="radar" size={16} color="var(--color-primary-hover)" /></span>
                  <span className="footer-ic-text">{addressLine}</span>
                </li>
              ) : null}
              {workingHours ? (
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="footer-ic" aria-hidden="true" style={{ display: 'inline-flex' }}><GlowIcon name="lightning" size={16} color="var(--color-primary-hover)" /></span>
                  <span className="footer-ic-text">{workingHours}</span>
                </li>
              ) : null}
              <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="footer-ic" aria-hidden="true" style={{ display: 'inline-flex' }}><GlowIcon name="chat" size={16} color="var(--color-primary-hover)" /></span>
                <a className="footer-ic-link" href={`mailto:${email}`}>{email}</a>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="footer-ic" aria-hidden="true" style={{ display: 'inline-flex' }}><GlowIcon name="chat" size={16} color="#20c997" /></span>
                <a
                  className="footer-ic-link"
                  href={whatsappDigits ? `https://wa.me/${whatsappDigits}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {whatsapp} (7/24 Canlı Destek)
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-bottom-inner">
            <span>© {new Date().getFullYear()}</span>
            <span className="footer-brand-name">{brandName}</span>
            <span className="footer-sep" aria-hidden="true">·</span>
            <span>Tüm hakları saklıdır</span>
          </p>
          <p className="footer-legal">
            Bu platform, yeni nesil dijital yayın altyapıları hakkında bilgilendirme ve paket tanıtımı amacıyla hazırlanmıştır. Teknik destek ve aktivasyon talepleriniz için WhatsApp hattımız üzerinden 7/24 hizmet alabilirsiniz.
          </p>
        </div>
      </div>
    </footer>
  );
};

/** Admin rotaları — indekslenmesin */
export const AdminSEO = ({ title = 'Admin Panel' }) => (
  <Helmet>
    <title>{title}</title>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

// SEO
export const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  structuredData,
  noindex = false
}) => {
  const fullUrl = url ? absoluteUrl(url) : SITE_URL;
  const fullImage = absoluteUrl(image || DEFAULT_OG_IMAGE);

  const { contactInfo } = useContent();
  const globalJsonLd = toJsonLd([
    buildOrganizationSchema(sanitizeStoredContact(contactInfo)),
    buildWebSiteSchema()
  ]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={SITE_NAME} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'}
      />
      <meta name="language" content="Turkish, German, English" />
      <link rel="canonical" href={fullUrl} />
      <link rel="alternate" hrefLang="tr-TR" href={fullUrl} />
      <link rel="alternate" hrefLang="de-DE" href={fullUrl} />
      <link rel="alternate" hrefLang="tr-DE" href={fullUrl} />
      <link rel="alternate" hrefLang="en-US" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {fullImage && (
        <>
          <meta property="og:image" content={fullImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </>
      )}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="tr_TR" />
      <meta property="og:locale:alternate" content="de_DE" />
      <meta property="og:locale:alternate" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content={fullImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {fullImage && <meta name="twitter:image" content={fullImage} />}

      {/* Additional SEO */}
      <meta name="theme-color" content="#0a0a0a" />
      <meta name="msapplication-TileColor" content="#5c1822" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {globalJsonLd && (
        <script type="application/ld+json">{JSON.stringify(globalJsonLd)}</script>
      )}

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(toJsonLd(structuredData))}
        </script>
      )}
    </Helmet>
  );
};

// FAQ Item
export const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={`faq-item glass-card-premium spotlight-card ${isOpen ? 'active' : ''}`}
      style={{
        marginBottom: '14px',
        borderRadius: '16px',
        border: isOpen ? '1px solid var(--color-primary-hover)' : '1px solid var(--color-border-glass)',
        transition: 'all 0.3s ease'
      }}
    >
      <div
        className="faq-header"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          cursor: 'pointer'
        }}
      >
        <h4 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 600, color: isOpen ? '#fff' : 'var(--color-text-main)' }}>{question}</h4>
        <div
          className="faq-icon"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: isOpen ? 'var(--color-primary)' : 'rgba(255,255,255,0.06)',
            color: '#fff',
            fontWeight: 700,
            transition: 'all 0.3s ease',
            flexShrink: 0
          }}
        >
          {isOpen ? '−' : '+'}
        </div>
      </div>
      {isOpen && (
        <div className="faq-content" style={{ padding: '0 24px 20px', color: 'var(--color-text-muted)', lineHeight: '1.7', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
          <p style={{ margin: 0 }}>{answer}</p>
        </div>
      )}
    </div>
  );
};

// Akıllı WhatsApp Mesaj Fonksiyonu
export const sendSmartWhatsAppMessage = (type, data = {}, contactInfo = null) => {
  const whatsappNumber = whatsappToWaDigits(contactInfo?.whatsapp);
  let message = '';

  switch (type) {
    case 'general':
      message = `*MERHABA! 👋*\n\n${SITE_NAME} hakkında bilgi almak istiyorum.\n\n`;
      if (data.question) message += `*Sorum:* ${data.question}\n`;
      break;

    case 'package':
      message = `*PAKET SATIN ALMA TALEBİ 📦*\n\n`;
      if (data.packageName) message += `*Paket:* ${data.packageName}\n`;
      if (data.duration) message += `*Süre:* ${data.duration}\n`;
      if (data.price) message += `*Fiyat:* ${data.price}\n`;
      message += `\nBu paketi satın almak istiyorum. Detaylı bilgi alabilir miyim?`;
      break;

    case 'test':
      message = `*ÜCRETSİZ TEST TALEBİ 🎁*\n\n`;
      message += `Merhaba, ${SITE_NAME}'i ücretsiz test etmek istiyorum.\n\n`;
      if (data.device) message += `*Cihazım:* ${data.device}\n`;
      if (data.question) message += `*Sorum:* ${data.question}\n`;
      break;

    case 'installation':
      message = `*KURULUM DESTEĞİ TALEBİ 🔧*\n\n`;
      message += `Merhaba, cihazımda kurulum desteği almak istiyorum.\n\n`;
      if (data.device) message += `*Cihaz:* ${data.device}\n`;
      if (data.tvBrand) message += `*TV Markası:* ${data.tvBrand}\n`;
      if (data.app) message += `*Kullanılan Uygulama:* ${data.app}\n`;
      if (data.question) message += `*Sorunum:* ${data.question}\n`;
      break;

    case 'reseller':
      message = `*BAYİLİK BAŞVURUSU 💼*\n\n`;
      message += `Merhaba, ${SITE_NAME} bayilik programı hakkında bilgi almak istiyorum.\n\n`;
      if (data.level) message += `*İlgilendiğim Seviye:* ${data.level}\n`;
      if (data.experience) message += `*Deneyimim:* ${data.experience}\n`;
      if (data.question) message += `*Sorum:* ${data.question}\n`;
      break;

    case 'support':
      message = `*TEKNİK DESTEK TALEBİ 🆘*\n\n`;
      message += `Merhaba, teknik destek almak istiyorum.\n\n`;
      if (data.issue) message += `*Sorunum:* ${data.issue}\n`;
      if (data.device) message += `*Cihaz:* ${data.device}\n`;
      if (data.details) message += `*Detaylar:* ${data.details}\n`;
      break;

    default:
      message = `*MERHABA! 👋*\n\n${SITE_NAME} hakkında bilgi almak istiyorum.`;
  }

  message += `\n\n*Sayfa:* ${window.location.pathname}`;
  message += `\n*Tarih:* ${new Date().toLocaleDateString('tr-TR')}`;

  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
  window.open(whatsappUrl, '_blank');
};

// Order Modal
export const OrderModal = ({ isOpen, onClose, packageInfo, isTest = false }) => {
  const { contactInfo } = useContent();
  const [selectedDevice, setSelectedDevice] = useState('smart-tv');
  const [tvBrand, setTvBrand] = useState('');
  const [appUsed, setAppUsed] = useState('');
  const [username, setUsername] = useState('');
  const [note, setNote] = useState('');

  const devices = [
    { id: 'smart-tv', name: 'Smart TV', iconName: 'tv' },
    { id: 'android-tv', name: 'Android TV', iconName: 'android' },
    { id: 'mobil', name: 'Mobil', iconName: 'mobile' },
    { id: 'pc-mac', name: 'PC/Mac', iconName: 'windows' },
    { id: 'mag-box', name: 'Mag/Box', iconName: 'download' }
  ];

  const handleWhatsApp = () => {
    const deviceName = devices.find(d => d.id === selectedDevice)?.name || selectedDevice;
    const packageName = packageInfo?.name || 'Paket';
    const packagePrice = packageInfo?.price || '';
    const packageDuration = packageInfo?.duration || '';

    let message = `*${isTest ? 'ÜCRETSİZ TEST' : 'PAKET SATIN ALMA'} TALEBİ*\n\n`;
    message += `📦 *Paket:* ${packageName}\n`;
    if (packageDuration) message += `⏱️ *Süre:* ${packageDuration}\n`;
    if (packagePrice) message += `💰 *Fiyat:* ${packagePrice}\n\n`;
    message += `📱 *Cihaz:* ${deviceName}\n`;
    if (tvBrand) message += `📺 *TV Markası:* ${tvBrand}\n`;
    if (appUsed) message += `🔧 *Kullanılan Uygulama:* ${appUsed}\n`;
    if (username) message += `👤 *Kullanıcı Adı:* ${username}\n`;
    if (note) message += `📝 *Not:* ${note}\n`;

    const whatsappNumber = whatsappToWaDigits(contactInfo?.whatsapp);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ backdropFilter: 'blur(8px)' }}>
      <div className="modal-content glass-card-premium spotlight-card" onClick={(e) => e.stopPropagation()} style={{ borderRadius: '24px', padding: '32px', border: '1px solid var(--color-border-glass)' }}>
        <button className="modal-close" onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '50%', width: '36px', height: '36px' }}>×</button>
        <h2 className="modal-title gradient-text-premium">{isTest ? 'Ücretsiz 24 Saat Test Talebi' : packageInfo?.name || 'Paket Seçimi'}</h2>
        <p className="modal-subtitle" style={{ color: 'var(--color-text-muted)', marginBottom: '24px' }}>Lütfen bilgilerinizi giriniz.</p>

        <div className="modal-form">
          <div className="form-section">
            <label className="form-label" style={{ fontWeight: 600, color: '#fff', marginBottom: '12px' }}>Cihaz Seçin</label>
            <div className="device-grid" style={{ gap: '10px' }}>
              {devices.map(device => (
                <div
                  key={device.id}
                  className={`device-card glass-card-premium ${selectedDevice === device.id ? 'active' : ''}`}
                  onClick={() => setSelectedDevice(device.id)}
                  style={{
                    padding: '14px',
                    borderRadius: '14px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: selectedDevice === device.id ? '2px solid var(--color-primary)' : '1px solid var(--color-border-glass)'
                  }}
                >
                  <div className="device-icon" style={{ marginBottom: '6px' }}><GlowIcon name={device.iconName} size={28} color={selectedDevice === device.id ? 'var(--color-primary-hover)' : '#ffffff'} /></div>
                  <div className="device-name" style={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff' }}>{device.name}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#fff' }}>
              <GlowIcon name="tv" size={18} color="var(--color-primary-hover)" /> <span>TV Markası (Opsiyonel)</span>
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Örn: Samsung, LG, Philips"
              value={tvBrand}
              onChange={(e) => setTvBrand(e.target.value)}
              style={{ background: 'rgba(20,20,30,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px 16px', color: '#fff' }}
            />
          </div>

          <div className="form-section">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#fff' }}>
              <GlowIcon name="settings" size={18} color="var(--color-primary-hover)" /> <span>Kullanılan Uygulama (Opsiyonel)</span>
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Örn: Smarters, VLC, Netiptv"
              value={appUsed}
              onChange={(e) => setAppUsed(e.target.value)}
              style={{ background: 'rgba(20,20,30,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px 16px', color: '#fff' }}
            />
          </div>

          <div className="form-section">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#fff' }}>
              <GlowIcon name="key" size={18} color="var(--color-primary-hover)" /> <span>Kullanıcı Adı (Tercihiniz)</span>
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Örn: netiptv_kullanici"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{ background: 'rgba(20,20,30,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px 16px', color: '#fff' }}
            />
          </div>

          <div className="form-section">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#fff' }}>
              <GlowIcon name="chat" size={18} color="var(--color-primary-hover)" /> <span>Notunuz (Opsiyonel)</span>
            </label>
            <textarea
              className="form-textarea"
              placeholder="Varsa özel bir isteğiniz..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows="4"
              style={{ background: 'rgba(20,20,30,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px 16px', color: '#fff', width: '100%' }}
            />
          </div>

          <button className="btn-premium-primary" style={{ width: '100%', marginTop: '16px' }} onClick={handleWhatsApp}>
            <GlowIcon name="chat" size={18} color="#ffffff" />
            <span>WhatsApp ile Talep Gönder</span>
          </button>
        </div>
      </div>
    </div>
  );
};
